/*
A Titan plan is sold per mailbox against a domain the customer is buying, and
lives in the cart as its own item keyed "<service_type>:<domain>". In the
summary it belongs inside that domain's box, shown like an ordinary mailbox, so
the two are paired off here before the items are rendered.
*/

// Mirrors Cart::UpdateItem::MAX_SUBSCRIPTION_QUANTITY,
// which is what actually enforces the cap.
const MAX_MAILBOXES = 10;

const MIN_MAILBOXES = 1;

// Only domain subscriptions (Titan) are sold per mailbox and tied to a domain;
// a Realnames subscription is a standalone single seat.
const isEmailSubscription = (item) =>
  String((item.components[0] || {}).service_type || "").startsWith("titan");

// A Titan tier upgrade (e.g. Pro -> Premium) is still a "titan" service_type,
// but it prices the difference for the subscription's existing seat count --
// unlike a fresh Titan purchase, its mailbox count isn't meant to be changed
// from the cart. The cart's item_type isn't serialized to the browser, but
// AddToCart#upgrade_add_to_cart always keys the item "<service_type>_upgrade:<domain>",
// so the cart key itself is the reliable signal here.
const isUpgradeItem = (item) => /_upgrade:/.test(item.name || "");

// A Titan "buy more accounts" item is keyed "<service_type>_accounts:<domain>"
// by AddToCart#accounts_increase_add_to_cart. Unlike a fresh purchase, its
// quantity is additive on top of the subscription's existing seats, so it
// can't be bounded by MAX_MAILBOXES alone -- see current_accounts below.
const isAccountsIncreaseItem = (item) => /_accounts:/.test(item.name || "");

const domainOf = (item) => item.display_name || item.name;

// A Titan plan can only be attached to a domain the customer is acquiring now.
// Renewals are left alone: those domains already have whatever email the
// customer set up for them.
const EMAIL_ELIGIBLE_EVENTS = ["registration", "transfer"];

const canTakeEmailPlan = (item) =>
  item.bundle || EMAIL_ELIGIBLE_EVENTS.includes(item.components[0].description);

/**
 * Split the cart into the items to render and the email subscription belonging
 * to each of them.
 *
 * A subscription whose domain is no longer in the cart stays in the list on its
 * own rather than disappearing, so the customer can still see and remove it.
 *
 * @param items {Array} cart items, as built by initCart
 * @return {{items: Array, byDomainItem: Object}} keyed by the host item's name
 */
function partitionEmailSubscriptions(items) {
  const hosts = items.filter((item) => !isEmailSubscription(item));
  const byDomainItem = {};

  items.filter(isEmailSubscription).forEach((subscription) => {
    const host = hosts.find((item) => domainOf(item) === domainOf(subscription));
    if (host) byDomainItem[host.name] = subscription;
  });

  const inlined = Object.values(byDomainItem).map((subscription) => subscription.name);

  return {
    items: items.filter((item) => !inlined.includes(item.name)),
    byDomainItem
  };
}

export {
  MAX_MAILBOXES,
  MIN_MAILBOXES,
  isEmailSubscription,
  isUpgradeItem,
  isAccountsIncreaseItem,
  partitionEmailSubscriptions,
  canTakeEmailPlan
};
